import { colorFromEmail } from '../functions/index.js'

const _toggleReaction = (el, s, postId, emoji) => {
  const me = el.call('findMe', s)
  const myName = me?.name || 'You'
  const next = (s.root.announcements || []).map((post) => {
    if (post.id !== postId) return post
    const reactions = { ...(post.reactions || {}) }
    const list = reactions[emoji] ? [...reactions[emoji]] : []
    const i = list.indexOf(myName)
    if (i === -1) list.push(myName)
    else list.splice(i, 1)
    if (list.length) reactions[emoji] = list
    else delete reactions[emoji]
    return { ...post, reactions }
  })
  s.root.update({ announcements: next })
}

// `overflow: visible` is required so the reaction picker popover (which
// is positioned absolutely below the + button) isn't clipped. The cover
// gradient block has its own `overflow: hidden` for rounded corners.
export const AnnouncementCard = {
  tag: 'article',
  flow: 'y',
  round: 'C',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  overflow: 'visible',
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 220ms ease',
  ':hover': {
    borderColor: 'blue',
    transform: 'translateY(-3px)',
    boxShadow: '0 24px 48px -24px rgba(0, 133, 255, 0.35)'
  },
  onClick: (e, el, s) => {
    // Ignore clicks that originated inside an interactive child (reactions, buttons, links)
    const t = e.target
    if (t && (t.closest('button') || t.closest('a'))) return
    s.root.update({ selectedAnnouncement: s })
  },

  AcCover: {
    height: 'F',
    background: (el, s) =>
      s.cover || 'linear-gradient(135deg, #0085FF 0%, #50E1FF 100%)',
    position: 'relative',
    overflow: 'hidden',
    borderTopLeftRadius: 'C',
    borderTopRightRadius: 'C',

    AcTagPill: {
      position: 'absolute',
      top: 'A',
      left: 'A',
      padding: 'Z A',
      round: 'B',
      fontSize: 'Y2',
      fontWeight: '700',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: '#0a0a0b',
      background: '#ffffff',
      text: (el, s) => s.tag || 'news'
    }
  },

  AcBody: {
    flow: 'y',
    gap: 'B',
    padding: 'C',

    AcHeader: {
      flow: 'y',
      gap: 'A',
      AcTitle: {
        tag: 'h2',
        fontSize: 'D',
        fontWeight: '800',
        color: 'title',
        letterSpacing: '-0.025em',
        lineHeight: '1.15',
        text: (el, s) => s.title
      },
      AcExcerpt: {
        tag: 'p',
        color: 'paragraph',
        fontSize: 'A',
        lineHeight: '1.55',
        text: (el, s) => s.excerpt
      }
    },

    AcAuthor: {
      flow: 'x',
      align: 'center',
      gap: 'A',
      paddingTop: 'A',
      borderTopWidth: '1px',
      borderTopStyle: 'solid',
      borderTopColor: 'line',

      AcAvatar: {
        align: 'center center',
        width: 'C',
        height: 'C',
        round: '50%',
        color: 'white',
        fontSize: 'Z2',
        fontWeight: '700',
        background: (el, s) => colorFromEmail(s.author),
        text: (el, s) => s.authorAvatar || ''
      },
      AcMeta: {
        flow: 'y',
        gap: 'Y',
        flex: '1',
        AcName: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          text: (el, s) => s.author
        },
        AcSub: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: (el, s) => {
            const parts = []
            if (s.authorRole) parts.push(s.authorRole)
            if (s.date) {
              try {
                parts.push(
                  new Date(s.date).toLocaleDateString('en', {
                    month: 'short',
                    day: 'numeric'
                  })
                )
              } catch {}
            }
            if (s.readTime) parts.push(s.readTime)
            return parts.join(' · ')
          }
        }
      }
    },

    AcReactions: {
      flow: 'x',
      align: 'center',
      gap: 'Z',
      flexWrap: 'wrap',
      AcExisting: {
        flow: 'x',
        align: 'center',
        gap: 'Z',
        flexWrap: 'wrap',
        ':empty': { display: 'none' },
        childExtends: 'ReactionPill',
        childrenAs: 'state',
        children: (el, s) =>
          Object.entries(s.reactions || {}).map(([emoji, names]) => ({
            emoji,
            names,
            postId: s.id
          }))
      },
      AcAddReaction: { extends: 'AddReactionPicker' }
    }
  }
}

export const ReactionPill = {
  tag: 'button',
  flow: 'x',
  align: 'center',
  gap: 'Z',
  padding: 'Y A',
  round: 'C',
  background: 'overlayStrong',
  borderWidth: '1px',
  borderStyle: 'solid',
  fontSize: 'Z2',
  fontWeight: '700',
  color: 'title',
  cursor: 'pointer',
  title: (el, s) => (s.names || []).join(', '),
  onClick: (ev, el, s) => {
    ev.preventDefault()
    _toggleReaction(el, s, s.postId, s.emoji)
  },
  // Border highlights when the signed-in user has reacted with this emoji.
  borderColor: (el, s) => {
    const myName = el.call('findMe', s)?.name
    return myName && (s.names || []).includes(myName) ? 'blue' : 'transparent'
  },
  RpEmoji: { tag: 'span', fontSize: 'A', text: (el, s) => s.emoji },
  RpCount: { tag: 'span', text: (el, s) => String((s.names || []).length) }
}

const POPULAR_EMOJIS = ['👍', '❤️', '🎉', '🚀', '🔥', '👏', '👀', '😂']
const ALL_EMOJIS = [
  '👍',
  '👎',
  '❤️',
  '🧡',
  '💛',
  '💚',
  '💙',
  '💜',
  '🎉',
  '🎊',
  '🚀',
  '🔥',
  '⚡',
  '✨',
  '💯',
  '⭐',
  '👏',
  '🙌',
  '🤝',
  '💪',
  '🙏',
  '👀',
  '😂',
  '😅',
  '😍',
  '🤩',
  '😎',
  '🤯',
  '🥳',
  '🤔',
  '😢',
  '😡',
  '✅',
  '❌',
  '⚠️',
  '💡',
  '📈',
  '📉',
  '📺',
  '🍕'
]

export const AddReactionPicker = {
  position: 'relative',

  ArpButton: {
    tag: 'button',
    align: 'center center',
    padding: 'Y A',
    round: 'C',
    background: 'overlayStrong',
    border: 'none',
    fontSize: 'A',
    fontWeight: '700',
    color: 'caption',
    cursor: 'pointer',
    text: '+',
    ':hover': { color: 'title' },
    // Imperative open. Each card creates its own stacking context, so we
    // also bump the open card's z-index above its siblings — otherwise
    // the popover is hidden behind the next row's cards.
    onClick: (ev, el) => {
      ev.preventDefault()
      ev.stopPropagation()
      const all = document.querySelectorAll('[data-arp-popover]')
      const mine = el.node.parentElement.querySelector('[data-arp-popover]')
      document.querySelectorAll('article').forEach((c) => {
        c.style.zIndex = ''
      })
      all.forEach((p) => {
        if (p !== mine) {
          p.style.opacity = '0'
          p.style.pointerEvents = 'none'
        }
      })
      const isOpen = mine.style.opacity === '1'
      mine.style.opacity = isOpen ? '0' : '1'
      mine.style.pointerEvents = isOpen ? 'none' : 'auto'
      const myCard = el.node.closest('article')
      if (myCard) myCard.style.zIndex = isOpen ? '' : '1000'
    }
  },

  // Toggle visibility instead of display: DOMQL prunes subtrees of
  // `display: none` on initial render, which would never mount the picker.
  ArpPopover: {
    flow: 'y',
    gap: 'A',
    position: 'absolute',
    top: 'D',
    left: 'auto',
    right: '0',
    zIndex: '100',
    width: 'H',
    maxWidth: '90vw',
    padding: 'B',
    round: 'C',
    background: 'overlayStrong',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    boxShadow: '0 24px 48px -16px rgba(0, 0, 0, 0.5)',
    opacity: '0',
    pointerEvents: 'none',
    transition: 'opacity 160ms ease',
    attr: { 'data-arp-popover': 'true' },

    ArpPopular: {
      flow: 'y',
      gap: 'Z',
      ArpPopHead: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'caption',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        text: 'Popular'
      },
      ArpPopGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gap: 'Z',
        childExtends: 'AddReactionEmoji',
        childrenAs: 'state',
        children: (el, s) =>
          POPULAR_EMOJIS.map((e) => ({ emoji: e, postId: s.id }))
      }
    },

    ArpAll: {
      flow: 'y',
      gap: 'Z',
      paddingTop: 'A',
      borderTopWidth: '1px',
      borderTopStyle: 'solid',
      borderTopColor: 'line',
      ArpAllHead: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'caption',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        text: 'All'
      },
      ArpAllGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gap: 'Z',
        childExtends: 'AddReactionEmoji',
        childrenAs: 'state',
        children: (el, s) => ALL_EMOJIS.map((e) => ({ emoji: e, postId: s.id }))
      }
    }
  }
}

export const AddReactionEmoji = {
  tag: 'button',
  align: 'center center',
  width: 'C',
  height: 'C',
  round: 'B',
  background: 'transparent',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'transparent',
  fontSize: 'B',
  cursor: 'pointer',
  text: (el, s) => s.emoji,
  ':hover': { background: 'overlay', borderColor: 'line' },
  onClick: (ev, el, s) => {
    ev.preventDefault()
    ev.stopPropagation()
    _toggleReaction(el, s, s.postId, s.emoji)
    document.querySelectorAll('[data-arp-popover]').forEach((p) => {
      p.style.opacity = '0'
      p.style.pointerEvents = 'none'
    })
    document.querySelectorAll('article').forEach((c) => {
      c.style.zIndex = ''
    })
  }
}

export const AnnouncementDetailModal = {
  extends: 'Modal',
  stateKey: 'selectedAnnouncement',

  Window: {
    extends: 'ModalWindow',
    maxWidth: '760px',
    width: '100%',
    maxHeight: '85vh',
    overflow: 'auto',

    AdmCover: {
      hide: (el, s) => !s.root?.selectedAnnouncement?.cover,
      height: 'G',
      flexShrink: '0',
      background: (el, s) =>
        s.root?.selectedAnnouncement?.cover ||
        'linear-gradient(135deg, #0085FF 0%, #50E1FF 100%)',
      borderTopLeftRadius: 'C',
      borderTopRightRadius: 'C'
    },

    AdmBody: {
      flow: 'y',
      gap: 'B',
      padding: 'D',

      AdmTagPill: {
        tag: 'span',
        alignSelf: 'flex-start',
        padding: 'Y A',
        round: 'C',
        fontSize: 'Y2',
        fontWeight: '700',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'blue',
        background: 'blue.2',
        text: (el, s) => s.root?.selectedAnnouncement?.tag || ''
      },

      AdmTitle: {
        tag: 'h1',
        margin: '0',
        fontSize: 'E',
        fontWeight: '800',
        letterSpacing: '-0.025em',
        lineHeight: '1.1',
        color: 'title',
        text: (el, s) => s.root?.selectedAnnouncement?.title || ''
      },

      AdmAuthor: {
        flow: 'x',
        align: 'center',
        gap: 'A',
        paddingBottom: 'B',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'line',

        AdmAvatar: {
          align: 'center center',
          width: 'C',
          height: 'C',
          round: '50%',
          color: 'white',
          fontSize: 'Z2',
          fontWeight: '700',
          background: (el, s) => colorFromEmail(s.root?.selectedAnnouncement?.author),
          text: (el, s) => s.root?.selectedAnnouncement?.authorAvatar || ''
        },
        AdmMeta: {
          flow: 'y',
          gap: 'Y',
          flex: '1',
          AdmAuthorName: {
            tag: 'span',
            fontSize: 'A',
            fontWeight: '700',
            color: 'title',
            text: (el, s) => s.root?.selectedAnnouncement?.author || ''
          },
          AdmAuthorSub: {
            tag: 'span',
            fontSize: 'Z2',
            color: 'caption',
            text: (el, s) => {
              const a = s.root?.selectedAnnouncement
              if (!a) return ''
              const parts = []
              if (a.authorRole) parts.push(a.authorRole)
              if (a.date) {
                try {
                  parts.push(
                    new Date(a.date).toLocaleDateString('en', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })
                  )
                } catch {}
              }
              if (a.readTime) parts.push(a.readTime)
              return parts.join(' · ')
            }
          }
        }
      },

      AdmExcerpt: {
        tag: 'p',
        margin: '0',
        fontSize: 'B',
        lineHeight: '1.55',
        color: 'paragraph',
        fontWeight: '500',
        text: (el, s) => s.root?.selectedAnnouncement?.excerpt || ''
      },

      AdmContent: {
        flow: 'y',
        gap: 'A',
        childExtends: 'AdmParagraph',
        childrenAs: 'state',
        children: (el, s) => {
          const body = s.root?.selectedAnnouncement?.body
          if (!body) return []
          if (Array.isArray(body)) return body.map((p) => ({ text: p }))
          if (typeof body === 'string') return [{ text: body }]
          return []
        }
      }
    }
  }
}

export const AdmParagraph = {
  tag: 'p',
  margin: '0',
  fontSize: 'A',
  lineHeight: '1.65',
  color: 'paragraph',
  text: (el, s) => s.text || ''
}

export const IntroRun = {
  tag: 'span',
  fontSize: 'A',
  color: (el, s) => s.color || 'caption',
  fontWeight: (el, s) => s.weight || '500',
  text: (el, s) => s.text
}

export const AvatarBundle = {
  display: 'flex',
  flexFlow: 'row',
  align: 'center',
  childExtends: 'AvatarBundleItem',
  childrenAs: 'state',
  children: (el, s) => {
    let emails = el.emails || []
    if (typeof emails === 'function') emails = emails(el, s) || []
    const lookup = el.call('peopleByEmail')
    const out = []
    for (const e of emails) {
      const p = lookup[e]
      if (p) out.push(p)
      if (out.length === 3) break
    }
    return out
  }
}

export const AvatarBundleItem = {
  position: 'relative',
  zIndex: '1',
  marginInlineEnd: '-Z1',
  cursor: 'default',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'overlay',
  round: '50%',

  Avatar: {
    avatarType: 'initials',
    boxSize: 'A1 A1',
    src: (el, s) => el.call('avatarUrl', (s && s.name) || (s && s.email))
  },

  onRender: (el, s) => {
    if (!el.node) return
    el.node.setAttribute('title', s.name || s.email || '')
    el.node.addEventListener('mouseenter', () => {
      el.node.style.zIndex = '50'
    })
    el.node.addEventListener('mouseleave', () => {
      el.node.style.zIndex = ''
    })
  }
}

const _emailByName = (s, name) => {
  if (!name) return null
  const list = (s && s.root && s.root.people) || []
  const lc = String(name).toLowerCase()
  for (const p of list) {
    if ((p.name || '').toLowerCase() === lc) return p.email
  }
  return null
}

export const BirthdayItem = {
  flow: 'x',
  align: 'center',
  gap: 'A',
  cursor: 'pointer',
  transition: 'transform 160ms ease',
  ':hover': { transform: 'translateY(-2px)' },
  onClick: (e, el, s) => {
    e.preventDefault()
    const email = _emailByName(s, s.name)
    if (email) s.root.update({ profileEmail: email })
  },

  BdAvatar: {
    align: 'center center',
    width: 'C',
    height: 'C',
    round: '50%',
    color: 'white',
    fontSize: 'Z2',
    fontWeight: '700',
    background: (el, s) => s.color || 'blue',
    text: (el, s) => s.initials || '',
    onRender: (el, s) => {
      if (el.node) el.node.setAttribute('title', s.name || '')
    }
  },
  BdName: {
    tag: 'span',
    fontSize: 'A',
    color: 'title',
    fontWeight: '600',
    text: (el, s) => s.name
  }
}

export const UpcomingEventCard = {
  extends: 'Link',
  href: (el, s) => `/calendar#event=${encodeURIComponent(s.id || '')}`,
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  flow: 'x',
  align: 'center',
  gap: 'A',
  padding: 'Z A',
  round: 'C',
  background: 'overlay',
  border: 'none',
  transition: 'all 200ms ease',
  ':hover': { background: 'overlayStrong', transform: 'translateY(-2px)' },

  UeDate: {
    flow: 'y',
    align: 'center center',
    width: 'C',
    minWidth: 'C',
    padding: 'Z',
    round: 'C',
    background: 'blue.2',
    UeDay: {
      tag: 'span',
      fontSize: 'B',
      fontWeight: '800',
      color: 'title',
      lineHeight: '1',
      text: (el, s) => {
        try {
          return String(new Date(s.startAt).getDate())
        } catch {
          return ''
        }
      }
    },
    UeMonth: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'blue',
      textTransform: 'uppercase',
      text: (el, s) => {
        try {
          return new Date(s.startAt).toLocaleString('en', { month: 'short' })
        } catch {
          return ''
        }
      }
    }
  },

  UeBody: {
    flow: 'y',
    gap: 'Y',
    flex: '1',
    minWidth: '0',
    UeTitle: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.title
    },
    UeMeta: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) => {
        let t = ''
        try {
          t = new Date(s.startAt).toLocaleTimeString('en', {
            hour: '2-digit',
            minute: '2-digit'
          })
        } catch {}
        return `${t}${t && s.location ? ' · ' : ''}${s.location || ''}`
      }
    }
  }
}

export const CityCluster = {
  extends: 'Link',
  href: '/people',
  textDecoration: 'none',
  color: 'inherit',
  flow: 'y',
  gap: 'Z1',
  minWidth: 'G',
  cursor: 'pointer',
  transition: 'transform 160ms ease',
  ':hover': { transform: 'translateY(-2px)' },

  CcStack: {
    extends: 'AvatarBundle',
    emails: (el, s) => (s.members || []).map((m) => m.email)
  },
  CcTime: {
    tag: 'span',
    fontSize: 'B',
    fontWeight: '700',
    color: 'title',
    text: (el, s) => {
      try {
        return new Date()
          .toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: s.timezone,
            hour12: true
          })
          .toLowerCase()
          .replace(' ', '')
      } catch {
        return ''
      }
    }
  },
  CcCity: {
    tag: 'span',
    fontSize: 'A',
    color: 'caption',
    text: (el, s) => s.city
  },
  CcDate: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'caption',
    text: () => {
      return new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      })
    }
  }
}
